import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDropzone } from 'react-dropzone';
import ExcelJS, { type CellValue } from 'exceljs';
import { cn } from '@/helpers/classnames';
import { toast } from '@/hooks/useToast';
import Button from '../core/Button';
import { useImportFileStore } from '@/stores/importFile';
import { FormSection } from '../FormSection';
import { Loading } from '../Loading';
import { FieldsLegend } from './FieldsLegend';
import {
  downloadCSVTemplate,
  downloadExcelTemplate,
  getStudentTemplateColumns,
} from '@/helpers/downloadTemplate';
import { parseCsv } from '@/helpers/csv';
import { trimImportCellValue } from '@/helpers/transformImportData';
import { IconCheck, IconDownload } from '@/icons';
import { StudentFormType } from '@/hooks/useStudentFormSchema';
import { useFieldMappings } from '@/hooks/useFieldMappings';
import { useHalfProductAvailability } from '@/hooks/useHalfProductAvailability';

export type Columns = {
  header: string;
  value: string;
};

interface ImportFileFormProps {
  type: StudentFormType;
}

const isCellNotEmpty = (cell: unknown): boolean => {
  return cell !== null && cell !== undefined && cell !== '';
};

const isRowNotEmpty = (row: unknown): boolean => {
  if (!Array.isArray(row)) return false;
  return row.some(isCellNotEmpty);
};

const getFirstNonEmptyCellInColumn = (
  rows: string[][],
  columnIndex: number,
): string => {
  for (const row of rows) {
    const cell = row[columnIndex];
    if (isCellNotEmpty(cell)) {
      return typeof cell === 'string' ? cell : String(cell);
    }
  }
  return '';
};

const formatExcelDate = (value: Date): string => {
  if (Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const excelCellToImportValue = (value: CellValue): unknown => {
  if (value === null || value === undefined) return undefined;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }
  if (value instanceof Date) return formatExcelDate(value);
  if ('richText' in value) {
    return value.richText.map((part) => part.text).join('');
  }
  if ('result' in value) {
    return excelCellToImportValue(value.result ?? null);
  }
  if ('text' in value) return value.text;
  if ('error' in value) return value.error;
  return undefined;
};

const worksheetToRows = (worksheet: ExcelJS.Worksheet): string[][] => {
  const rawRows: CellValue[][] = [];
  let columnCount = 0;

  worksheet.eachRow({ includeEmpty: true }, (row) => {
    const values = row.values;
    const cells = Array.isArray(values) ? values.slice(1) : [];
    columnCount = Math.max(columnCount, cells.length);
    rawRows.push(cells);
  });

  if (columnCount === 0) return [];

  return rawRows.map((cells) =>
    Array.from({ length: columnCount }, (_, index) =>
      trimImportCellValue(excelCellToImportValue(cells[index])),
    ),
  );
};

const readImportRows = async (file: File): Promise<string[][]> => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'csv') {
    return parseCsv(await file.text()).map((row) =>
      row.map((cell) => trimImportCellValue(cell)),
    );
  }

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const worksheet = workbook.worksheets[0];
  if (!worksheet) {
    throw new Error('No sheets found in the Excel file');
  }
  return worksheetToRows(worksheet);
};

export const ImportFileForm = ({ type }: ImportFileFormProps) => {
  const { formatMessage } = useIntl();
  const isHalfProductAvailable = useHalfProductAvailability();
  const disableHalfProduct = !isHalfProductAvailable(type);
  const {
    import: importData,
    reset,
    update: updateFileData,
  } = useImportFileStore();

  const { getRequiredFields } = useFieldMappings(type);

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (incomingFiles) => {
      // Rejected files still trigger onDrop with an empty accepted list.
      const file = incomingFiles[0];
      if (!file) return;
      onDropFile(file);
    },
    onDropRejected: (rejections) => {
      const fileName = rejections[0]?.file.name ?? '';
      toast.warning({
        title: formatMessage(
          {
            defaultMessage:
              '{fileName} har ett ogiltigt filformat. Tillåtna format: CSV (.csv), Excel (.xlsx)',
          },
          { fileName },
        ),
      });
    },
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'text/csv': ['.csv'],
    },
    multiple: false,
  });

  // Extract data from the imported Excel for mapping and error handling
  const onDropFile = useCallback(
    async (file: File) => {
      reset();
      setErrorMessage('');
      setIsProcessing(true);

      try {
        const data = await readImportRows(file);
        if (!data.length || data[0].length === 0) {
          setErrorMessage(
            formatMessage({
              defaultMessage: 'Excelfilen är tom eller saknar rubriker',
            }),
          );
          setIsProcessing(false);
          throw new Error('Excel file is empty or missing headers');
        }

        const nonEmptyRows = data.slice(1).filter(isRowNotEmpty);

        const headers = data[0] as string[];
        const rows = nonEmptyRows as string[][];
        const columns = headers.map((header, idx) => ({
          header: header,
          value: getFirstNonEmptyCellInColumn(rows, idx),
        }));

        // Validate that the file has enough columns for all required fields
        const requiredFieldsCount = getRequiredFields.length;
        const nonEmptyColumns = columns.filter((col) => col.header.length > 0);

        if (nonEmptyColumns.length < requiredFieldsCount) {
          setErrorMessage(
            formatMessage(
              {
                defaultMessage:
                  'Filen har för få kolumner ({found}) för att matcha alla obligatoriska fält ({required}). Ladda upp en fil med fler kolumner.',
              },
              { found: nonEmptyColumns.length, required: requiredFieldsCount },
            ),
          );
          setIsProcessing(false);
          return;
        }

        // Set extracted data to use in the ImportColumnMapper component
        updateFileData({
          file: {
            importHeaders: headers,
            importRows: rows,
            importColumns: columns,
          },
        });
        setIsProcessing(false);
      } catch (err: unknown) {
        if (
          err instanceof Error &&
          err.message === 'No sheets found in the Excel file'
        ) {
          setErrorMessage(
            formatMessage({
              defaultMessage: 'Inget blad kunde hittas i filen',
            }),
          );
        }
        if (err instanceof Error) {
          console.error('Error parsing Excel file: ', err.message);
        }
        setIsProcessing(false);
      }
    },
    [reset, formatMessage, getRequiredFields.length, updateFileData],
  );

  const handleRetry = useCallback(() => {
    reset();
    setErrorMessage('');
  }, [reset]);

  const renderFileDropStatus = useCallback(() => {
    return (
      <span className="text-text-700">
        {formatMessage({
          defaultMessage:
            'Dra och släpp eller klicka för att välja fil för import',
        })}
      </span>
    );
  }, [formatMessage]);

  const translateExtraInfo = useCallback(
    (id: string): string => {
      const messages: Record<string, string> = {
        'import.guardianAddressInfo.requiredForGuardian1': formatMessage({
          id: 'import.guardianAddressInfo.requiredForGuardian1',
        }),
        'import.guardianAddressInfo.usedForTravelSearch': formatMessage({
          id: 'import.guardianAddressInfo.usedForTravelSearch',
        }),
        'import.guardianAddressInfo.busStopOptional': formatMessage({
          id: 'import.guardianAddressInfo.busStopOptional',
        }),
        'import.deliveryAddressInfo.cardDelivery': formatMessage({
          id: 'import.deliveryAddressInfo.cardDelivery',
        }),
      };
      return messages[id] ?? id;
    },
    [formatMessage],
  );

  const handleDownloadCSVTemplate = useCallback(() => {
    const columns = getStudentTemplateColumns(
      type,
      disableHalfProduct,
      translateExtraInfo,
    );
    downloadCSVTemplate('elever_mall.csv', columns);
  }, [type, disableHalfProduct, translateExtraInfo]);

  const handleDownloadExcelTemplate = useCallback(async () => {
    try {
      const columns = getStudentTemplateColumns(
        type,
        disableHalfProduct,
        translateExtraInfo,
      );
      await downloadExcelTemplate('elever_mall.xlsx', columns);
    } catch (err) {
      console.error('Failed to generate Excel template', err);
      toast.error({
        title: formatMessage({
          defaultMessage: 'Kunde inte skapa Excel-mall',
        }),
      });
    }
  }, [type, disableHalfProduct, formatMessage, translateExtraInfo]);

  const renderErrorMessage = useCallback(() => {
    if (!errorMessage) return;

    return (
      <div
        role="alert"
        className="w-full items-center rounded-md border-2 border-dashed border-red-600 p-4"
      >
        <span className="flex justify-center text-red-700">{errorMessage}</span>
      </div>
    );
  }, [errorMessage]);

  if (isProcessing) {
    return <Loading />;
  }

  return (
    <form className="flex size-full flex-col gap-4">
      {!importData.file && (
        <FormSection
          title={formatMessage({
            defaultMessage: 'Välj fil att importera',
          })}
        >
          <div
            {...getRootProps({
              className: cn(
                'flex border border-dashed border-2 border-background-400 rounded-md p-8 items-center gap-4 flex-col hover:bg-background-100 cursor-pointer',
                isDragActive && 'bg-background-100',
              ),
            })}
          >
            <input
              {...getInputProps()}
              aria-label={formatMessage({
                defaultMessage: 'Välj fil för import',
              })}
              aria-describedby="import-file-formats"
            />
            {renderFileDropStatus()}
          </div>
          <p id="import-file-formats" className="mt-2 text-sm text-text-700">
            <FormattedMessage defaultMessage="Tillåtna filformat: CSV (.csv), Excel (.xlsx)" />
          </p>
          {errorMessage && (
            <div className="flex flex-col items-end justify-end gap-2">
              {renderErrorMessage()}
            </div>
          )}
        </FormSection>
      )}
      {!importData.file && (
        <FormSection>
          <div className="mb-4 flex gap-2">
            <Button
              variant="outline"
              startIcon={<IconDownload />}
              onClick={handleDownloadExcelTemplate}
            >
              <FormattedMessage defaultMessage="Ladda ner Excel-mall" />
            </Button>
            <Button
              variant="outline"
              startIcon={<IconDownload />}
              onClick={handleDownloadCSVTemplate}
            >
              <FormattedMessage defaultMessage="Ladda ner CSV-mall" />
            </Button>
          </div>
          {type !== 'child-and-youth' && (
            <>
              <p className="text-text-700">
                <FormattedMessage id="import.guardianAddressInfo.requiredForGuardian1" />
              </p>
              <p className="text-text-700">
                <FormattedMessage id="import.guardianAddressInfo.usedForTravelSearch" />
              </p>
              <p className="text-text-700">
                <FormattedMessage id="import.guardianAddressInfo.busStopOptional" />
              </p>
            </>
          )}
          <p className="text-text-700">
            <FormattedMessage id="import.deliveryAddressInfo.cardDelivery" />
          </p>
          <p className="text-text-700">
            <FormattedMessage defaultMessage="Systemet har stöd för 'E-post i appen / Mina sidor' på resenärer, men detta sätter de själva genom självbetjäningen eller manuellt av en admin. Det är inget som kan ändras via importen." />
          </p>
          <FieldsLegend type={type} />
        </FormSection>
      )}
      {importData.file?.importColumns && !errorMessage && (
        <FormSection title={formatMessage({ defaultMessage: 'Fil uppladdad' })}>
          <div className="flex items-center gap-3 rounded-md border border-primary-300 bg-background-100 p-4">
            <div className="flex size-10 items-center justify-center rounded-full bg-primary-500">
              <IconCheck className="text-contrast-primary" />
            </div>
            <div className="flex flex-col">
              <span className="font-medium text-text-900">
                <FormattedMessage
                  defaultMessage="{count} kolumner hittades i filen"
                  values={{ count: importData.file.importColumns.length }}
                />
              </span>
              <span className="text-sm text-text-700">
                <FormattedMessage defaultMessage="Klicka på Nästa för att matcha kolumnerna med systemfält" />
              </span>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleRetry}
            className="ml-auto mt-4"
          >
            {formatMessage({ defaultMessage: 'Välj annan fil' })}
          </Button>
        </FormSection>
      )}
    </form>
  );
};
